import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import MiniSpin from "@/components/MiniSpin";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  BadgeCheck,
  Check,
  Copy,
  CreditCard,
  Loader2,
  Lock,
  RotateCcw,
  ShieldCheck,
} from "lucide-react";
import { Reveal, EASE } from "@/components/Reveal";
import TestDriveScheduler from "@/components/TestDriveScheduler";
import BookingLookup from "@/components/BookingLookup";
import AuthForms from "@/components/AuthPanel";
import { api, formatINR } from "@/lib/api";

const ISHUSTAR_PRICE = 25524000;

const VARIANTS = [
  { id: "ONE", tier: "Premium", ex: "₹12.74 Lakh*", was: "₹14.99 Lakh*" },
  { id: "PRO", tier: "Performance", ex: "₹16.99 Lakh*", was: "₹19.99 Lakh*" },
  { id: "X", tier: "Adventure", ex: "₹21.24 Lakh*", was: "₹24.99 Lakh*" },
  { id: "LUXE", tier: "Luxury", ex: "₹25.49 Lakh*", was: "₹29.99 Lakh*" },
];

// ISHUCAR SUV — ₹50 Lakh brochure ladder (no launch discount)
const SUV_VARIANTS = [
  { id: "EXPLORER", tier: "Essential 4x4 AWD", ex: "₹50 Lakh*", was: null },
  { id: "ADVENTURE", tier: "Off-Road Pack · Air Suspension", ex: "₹55 Lakh*", was: null },
  { id: "SIGNATURE", tier: "Full Luxury · ADAS", ex: "₹60 Lakh*", was: null },
];

const MODEL_CONFIG = {
  "aero-d": {
    id: "Aero D",
    name: "IshuCar AERO D",
    tag: "Crossover SUV · Diesel",
    token: 10000,
    variants: VARIANTS,
    hero: "/images/aero-hero.jpg",
    spinPrefix: "spin-aero",
    ev: false,
    colors: [
      { id: "Black", name: "Black", swatch: "#101014", ext: "/images/aero-black.jpg", int: "/images/cockpit.jpg" },
      { id: "White", name: "White", swatch: "#e8e9ed", ext: "/images/aero-white.jpg", int: "/images/panel-interior.jpg" },
      { id: "Silver", name: "Silver", swatch: "#c7c9d1", ext: "/images/aero-silver.jpg", int: "/images/seats.jpg" },
    ],
  },
  suv: {
    id: "SUV",
    name: "IshuCar SUV",
    tag: "Off-Road 4x4 AWD · Diesel · 200-500 HP",
    token: 10000,
    variants: SUV_VARIANTS,
    discount: false,
    hero: "/images/suv-forest.jpg",
    spinPrefix: null,
    ev: false,
    colors: [
      { id: "Forest Green", name: "Forest Green", swatch: "#5e6b4a", ext: "/images/suv-forest.jpg", int: "/images/suv-dashboard.jpg" },
      { id: "Midnight Black", name: "Midnight Black", swatch: "#0f0f14", ext: "/images/suv-midnight.jpg", int: "/images/suv-dashboard.jpg" },
      { id: "Glacier White", name: "Glacier White", swatch: "#e8e9ed", ext: "/images/suv-glacier.jpg", int: "/images/suv-dashboard.jpg" },
      { id: "Desert Sand", name: "Desert Sand", swatch: "#c2a878", ext: "/images/suv-sand.jpg", int: "/images/suv-dashboard.jpg" },
      { id: "Steel Grey", name: "Steel Grey", swatch: "#8b9096", ext: "/images/suv-steel.jpg", int: "/images/suv-dashboard.jpg" },
      { id: "Matte Black", name: "Matte Black", swatch: "#1c1c1e", ext: "/images/suv-matte.jpg", int: "/images/suv-dashboard.jpg" },
    ],
  },
  electric: {
    id: "Electric",
    name: "IshuCar Electric",
    tag: "100% Electric · 700+ km Range",
    token: 10000,
    variants: VARIANTS,
    hero: "/images/ev-hero.jpg",
    spinPrefix: "spin",
    ev: true,
    colors: [
      { id: "White", name: "Pearl White", swatch: "#e8e9ed", ext: "/images/ev-hero.jpg", int: "/images/panel-interior.jpg" },
      { id: "Black", name: "Black", swatch: "#101014", ext: "/images/ev-black.jpg", int: "/images/cockpit.jpg" },
      { id: "Silver", name: "Silver", swatch: "#c7c9d1", ext: "/images/ev-silver.jpg", int: "/images/seats.jpg" },
    ],
  },
  ishustar: {
    id: "Ishustar",
    name: "IshuCar ISHUSTAR",
    tag: "100% Electric Hypercar",
    token: 500000,
    price: ISHUSTAR_PRICE,
    noVariants: true,
    variants: [{ id: "STD", tier: "Hypercar", ex: "₹2,55,24,000*", was: null }],
    hero: "/images/ishustar-hero.jpg",
    spinPrefix: null,
    ev: true,
    colors: [
      { id: "Silver", name: "Liquid Silver", swatch: "#c7c9d1", ext: "/images/ishustar-hero.jpg", int: "/images/ishustar-cockpit.jpg" },
    ],
  },
};

const EV_FEATURES = [
  "100% Electric",
  "700+ km Range",
  "Fast Charging · 80% in 30 min",
  "AI Copilot",
  "Self-Driving Mode",
  "Solar Assist",
  "Zero Emissions",
];

const HUB_MODELS = [
  { slug: "aero-d", ...MODEL_CONFIG["aero-d"] },
  { slug: "suv", ...MODEL_CONFIG.suv },
  { slug: "electric", ...MODEL_CONFIG.electric },
  { slug: "ishustar", ...MODEL_CONFIG.ishustar },
];

const PERKS = [
  { icon: ShieldCheck, title: "Fully refundable", desc: "Cancel anytime. The token comes back to you — no questions." },
  { icon: BadgeCheck, title: "Priority queue", desc: "Your place in line is locked the moment you reserve." },
  { icon: RotateCcw, title: "First test drives", desc: "Reserved owners get the earliest test drive invitations." },
];

function Steps({ step }) {
  const steps = ["Details", "Payment", "Confirmed"];
  const activeIndex = steps.indexOf(step);
  return (
    <div className="flex items-center gap-0" data-testid="booking-steps">
      {steps.map((s, i) => (
        <div key={s} className="flex items-center">
          <div className="flex items-center gap-2.5">
            <span
              className={`flex h-8 w-8 items-center justify-center rounded-full border font-mono2 text-[12px] transition-all duration-300 ${
                i < activeIndex
                  ? "border-[#e6b800] bg-[#e6b800] text-[#070709]"
                  : i === activeIndex
                    ? "border-[#e6b800] text-[#e6b800]"
                    : "border-white/15 text-neutral-600"
              }`}
            >
              {i < activeIndex ? <Check size={13} /> : i + 1}
            </span>
            <span className={`hidden font-mono2 text-[11px] uppercase tracking-[0.2em] sm:block ${i === activeIndex ? "text-white" : "text-neutral-600"}`}>
              {s}
            </span>
          </div>
          {i < steps.length - 1 && <span className="mx-3 h-px w-8 bg-white/15 sm:w-14" />}
        </div>
      ))}
    </div>
  );
}

function DetailsForm({ modelConfig, onDone, user }) {
  const variants = modelConfig.variants;
  const [form, setForm] = useState({ name: user?.name || "", email: user?.email || "", phone: "" });
  const [variant, setVariant] = useState(modelConfig.noVariants ? "STD" : variants[0].id);
  const [color, setColor] = useState(modelConfig.colors[0].id);
  const [loading, setLoading] = useState(false);
  const activeColor = modelConfig.colors.find((c) => c.id === color) || modelConfig.colors[0];

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post("/bookings", { ...form, model: modelConfig.id, variant, color });
      onDone(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create booking. Try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-white/10 bg-[#0a0a0e] px-4 py-3.5 text-white placeholder:text-neutral-600 outline-none transition-colors focus:border-[#e6b800]/70";

  return (
    <form onSubmit={submit} data-testid="booking-details-form" className="space-y-5">
      <div className="overflow-hidden rounded-xl border border-[#e6b800]/30 bg-[#0a0a0e]" data-testid="booking-model-banner">
        <div className="relative">
          <img src={modelConfig.hero} alt={modelConfig.name} className="h-36 w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0e] via-transparent to-transparent" />
          <div className="absolute bottom-2 left-4 right-4 flex items-end justify-between gap-3">
            <div>
              <p className="font-display text-lg font-bold text-white">{modelConfig.name}</p>
              <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#e6b800]">{modelConfig.tag}</p>
            </div>
            <Link to="/booking" className="shrink-0 font-mono2 text-[10px] uppercase tracking-[0.15em] text-neutral-400 underline-offset-4 transition-colors hover:text-[#e6b800] hover:underline" data-testid="booking-change-model">
              बदलें
            </Link>
          </div>
        </div>
        {modelConfig.ev && (
          <div className="flex flex-wrap gap-1.5 border-t border-white/10 px-4 py-3" data-testid="ev-features">
            {EV_FEATURES.map((f) => (
              <span key={f} className="rounded-full border border-[#e6b800]/30 bg-[#e6b800]/5 px-2.5 py-1 text-[10px] font-semibold text-[#e6b800]">
                {f}
              </span>
            ))}
          </div>
        )}
      </div>

      {modelConfig.noVariants ? (
        <div className="rounded-xl border border-[#e6b800]/25 bg-[#e6b800]/5 px-4 py-3" data-testid="booking-single-variant-note">
          <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#e6b800]">1 · Variant</p>
          <p className="mt-1 text-sm text-neutral-300">
            Single variant — <span className="font-semibold text-white">STD · ₹2,55,24,000*</span> limited availability.
          </p>
        </div>
      ) : (
      <div>
        <label className="mb-2 block font-mono2 text-[11px] uppercase tracking-[0.2em] text-neutral-400">
          1 · Variant चुनें
        </label>
        <div className="grid grid-cols-2 gap-3" data-testid="variant-picker">
          {variants.map((v, i) => (
            <button
              type="button"
              key={v.id}
              data-testid={`booking-variant-${i}`}
              onClick={() => setVariant(v.id)}
              className={`rounded-xl border px-4 py-3 text-left transition-all duration-200 ${
                variant === v.id
                  ? "border-[#e6b800] bg-[#e6b800]/10"
                  : "border-white/10 bg-[#0a0a0e] hover:border-[#e6b800]/50"
              }`}
            >
              <p className="font-display text-sm font-bold text-white">{v.id}</p>
              <p className={`mt-0.5 font-mono2 text-[10px] uppercase tracking-[0.18em] ${variant === v.id ? "text-[#e6b800]" : "text-neutral-500"}`}>
                {v.tier}
              </p>
              <p className={`mt-1 text-[11px] font-semibold ${variant === v.id ? "text-white" : "text-neutral-400"}`}>
                {v.was && <s className="mr-1.5 text-neutral-500">{v.was}</s>}{v.ex}
                {modelConfig.discount !== false && (
                  <span className="ml-1.5 rounded bg-[#e6b800]/15 px-1 py-0.5 font-mono2 text-[9px] font-bold text-[#e6b800]">15% OFF</span>
                )}
              </p>
            </button>
          ))}
        </div>
      </div>
      )}

      <div>
        <label className="mb-2 block font-mono2 text-[11px] uppercase tracking-[0.2em] text-neutral-400">
          Choose your colour
        </label>
        <div className="flex flex-wrap gap-3" data-testid="booking-color-picker">
          {modelConfig.colors.map((c) => (
            <button
              type="button"
              key={c.id}
              data-testid={`booking-color-${c.id.toLowerCase()}`}
              onClick={() => setColor(c.id)}
              className={`flex items-center gap-2 rounded-full border px-4 py-2 transition-all duration-200 ${
                color === c.id
                  ? "border-[#e6b800] bg-[#e6b800]/10"
                  : "border-white/10 hover:border-white/40"
              }`}
            >
              <span className="h-5 w-5 rounded-full border border-white/30" style={{ background: c.swatch }} />
              <span className={`font-mono2 text-[11px] uppercase tracking-[0.15em] ${color === c.id ? "text-[#e6b800]" : "text-neutral-400"}`}>
                {c.name}
              </span>
            </button>
          ))}
        </div>
      </div>

      <div className="overflow-hidden rounded-xl border border-white/10" data-testid="booking-color-preview">
        <div className="grid grid-cols-3">
          <div className="relative col-span-2">
            <AnimatePresence mode="wait">
              <motion.img
                key={`ext-${color}`}
                src={activeColor.ext}
                alt={`IshuCar exterior in ${color}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.35 }}
                className="h-40 w-full object-cover sm:h-48"
                data-testid="preview-exterior-image"
              />
            </AnimatePresence>
            <span className="absolute bottom-2 left-2 rounded-full bg-black/60 px-2.5 py-1 font-mono2 text-[9px] uppercase tracking-[0.2em] text-white">
              Exterior
            </span>
          </div>
          <div className="relative">
            <AnimatePresence mode="wait">
              <motion.img
                key={`int-${color}`}
                src={activeColor.int}
                alt={`IshuCar interior in ${color}`}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.35 }}
                className="h-40 w-full object-cover sm:h-48"
                data-testid="preview-interior-image"
              />
            </AnimatePresence>
            <span className="absolute bottom-2 left-2 rounded-full bg-black/60 px-2.5 py-1 font-mono2 text-[9px] uppercase tracking-[0.2em] text-white">
              Interior
            </span>
          </div>
        </div>
        <p className="bg-[#111116] px-4 py-2.5 font-mono2 text-[10px] uppercase tracking-[0.2em] text-neutral-500">
          Preview — {variant} · {color}
        </p>
        {modelConfig.spinPrefix ? (
          <MiniSpin key={modelConfig.id} prefix={modelConfig.spinPrefix} name={modelConfig.name} />
        ) : null}
      </div>

      <div>
        <label htmlFor="bk-name" className="mb-2 block font-mono2 text-[11px] uppercase tracking-[0.2em] text-neutral-400">
          Full name
        </label>
        <input
          id="bk-name"
          data-testid="booking-name-input"
          className={inputCls}
          placeholder="Aarav Sharma"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          required
          minLength={2}
        />
      </div>
      <div>
        <label htmlFor="bk-email" className="mb-2 block font-mono2 text-[11px] uppercase tracking-[0.2em] text-neutral-400">
          Email
        </label>
        <input
          id="bk-email"
          data-testid="booking-email-input"
          type="email"
          className={`${inputCls} cursor-not-allowed opacity-80`}
          value={form.email}
          readOnly
          required
        />
        <p className="mt-2 text-[11px] leading-relaxed text-neutral-500">
          Email आपके account से लिया गया — confirmation और आपकी bookings इसी पर दिखेंगी।
        </p>
      </div>
      <div>
        <label htmlFor="bk-phone" className="mb-2 block font-mono2 text-[11px] uppercase tracking-[0.2em] text-neutral-400">
          Phone
        </label>
        <input
          id="bk-phone"
          data-testid="booking-phone-input"
          type="tel"
          className={inputCls}
          placeholder="+91 98765 43210"
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
          required
          minLength={7}
        />
      </div>

      <button
        type="submit"
        data-testid="booking-submit-button"
        disabled={loading}
        className="group flex w-full items-center justify-center gap-2 rounded-full bg-[#e6b800] px-6 py-4 font-bold text-[#070709] transition-all duration-200 hover:bg-[#ffc700] hover:shadow-[0_0_36px_rgba(230,184,0,0.4)] disabled:opacity-60"
      >
        {loading ? <Loader2 size={17} className="animate-spin" /> : <Lock size={15} />}
        {loading ? "Reserving…" : `Continue to payment — ${formatINR(modelConfig.token)}`}
      </button>
      <p className="text-center text-xs text-neutral-500">
        Step 1 of 2 · Your details stay private and are used only for your reservation.
      </p>
    </form>
  );
}

function RazorpayCheckout({ booking, onPaid, onBack }) {
  const [paying, setPaying] = useState(false);

  const openCheckout = async () => {
    if (!window.Razorpay) {
      toast.error("Payment gateway load nahi hua — page refresh karke dobara try karein.");
      return;
    }
    setPaying(true);
    try {
      const res = await api.post("/create-order", { booking_id: booking.id });
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: res.data.amount,
        currency: res.data.currency,
        order_id: res.data.order_id,
        name: "IshuCar",
        description: `Pre-booking token · ${booking.model} ${booking.variant}`,
        prefill: { name: booking.name, email: booking.email, contact: booking.phone },
        theme: { color: "#e6b800" },
        handler: async (r) => {
          try {
            const vf = await api.post("/verify-payment", {
              booking_id: booking.id,
              razorpay_order_id: r.razorpay_order_id,
              razorpay_payment_id: r.razorpay_payment_id,
              razorpay_signature: r.razorpay_signature,
            });
            onPaid(vf.data);
          } catch (err) {
            toast.error(err?.response?.data?.detail || "Payment verify nahi hua — support@ishukart.com par email karein.");
            setPaying(false);
          }
        },
        modal: {
          ondismiss: () => {
            setPaying(false);
            toast.info("Payment cancel ho gaya — jab chahein dobara try karein.");
          },
        },
      };
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", () => {
        toast.error("Payment fail ho gaya. Amount kata ho to 5–7 din me wapas aa jayega.");
        setPaying(false);
      });
      rzp.open();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not start payment. Please try again.");
      setPaying(false);
    }
  };

  return (
    <div data-testid="razorpay-checkout">
      <div className="card-glow rounded-2xl p-6 sm:p-8">
        <div className="flex items-center justify-between border-b border-white/10 pb-5">
          <div className="flex items-center gap-2.5">
            <CreditCard size={18} className="text-[#e6b800]" />
            <p className="font-display text-sm font-bold uppercase tracking-[0.15em] text-white">IshuCar Secure Checkout</p>
          </div>
          <span className="flex items-center gap-1.5 font-mono2 text-[10px] uppercase tracking-[0.2em] text-neutral-500">
            <ShieldCheck size={11} /> 100% Refundable
          </span>
        </div>

        <div className="flex items-center justify-between py-5">
          <div>
            <p className="text-sm text-neutral-400">{booking.model} {booking.variant} · {booking.color} · Pre-booking token</p>
            <p className="mt-0.5 font-mono2 text-[11px] uppercase tracking-[0.15em] text-neutral-600">
              Ref {booking.reference}
            </p>
          </div>
          <p className="font-display text-xl font-bold text-white">{formatINR(booking.token_amount)}</p>
        </div>

        {booking.model === "Ishustar" && (
          <div className="space-y-3 rounded-xl border border-white/10 bg-[#0a0a0e] p-4" data-testid="ishustar-payment-breakdown">
            <div className="flex items-center justify-between text-sm">
              <div>
                <p className="font-display text-base font-bold text-white">Due Today</p>
                <p className="text-[11px] text-neutral-500">Fully refundable</p>
              </div>
              <p className="font-mono2 text-lg font-semibold text-[#e6b800]" data-testid="ishustar-due-today">
                {formatINR(booking.token_amount)}
              </p>
            </div>
            <div className="flex items-center justify-between text-sm">
              <p className="font-display text-base font-bold text-white">Due within 10 days</p>
              <p className="font-mono2 text-lg font-semibold text-white" data-testid="ishustar-due-later">
                {formatINR(ISHUSTAR_PRICE - booking.token_amount)}
              </p>
            </div>
            <p className="text-[11px] leading-relaxed text-neutral-500">
              Reservations are not final until payment is received. Reservation subject to cancellation if payment is
              not completed within 10 days. Balance {formatINR(ISHUSTAR_PRICE - booking.token_amount)} IshuCar team
              द्वारा बाकी delivery पर लिया जाएगा।
            </p>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {["UPI", "Cards", "NetBanking", "Wallets"].map((m) => (
            <span key={m} className="rounded-full border border-white/10 px-3 py-1 font-mono2 text-[10px] uppercase tracking-[0.15em] text-neutral-400">
              {m}
            </span>
          ))}
        </div>

        <button
          onClick={openCheckout}
          data-testid="checkout-pay-button"
          disabled={paying}
          className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-[#e6b800] px-6 py-4 font-bold text-[#070709] transition-all duration-200 hover:bg-[#ffc700] hover:shadow-[0_0_36px_rgba(230,184,0,0.4)] disabled:opacity-60"
        >
          {paying ? <Loader2 size={17} className="animate-spin" /> : <Lock size={15} />}
          {paying ? "Opening secure checkout…" : `Pay ${formatINR(booking.token_amount)} securely`}
        </button>

        <p className="mt-4 text-center text-xs leading-relaxed text-neutral-500">
          Payments secured by Razorpay. Token 100% refundable hai — cancellation par 7–10 din me wapas.
        </p>
      </div>

      <button
        onClick={onBack}
        data-testid="checkout-back-button"
        className="mt-5 inline-flex items-center gap-2 font-mono2 text-[11px] uppercase tracking-[0.2em] text-neutral-500 transition-colors hover:text-white"
      >
        <ArrowLeft size={14} /> Back to details
      </button>
    </div>
  );
}

function Confirmed({ booking }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(booking.reference).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.96 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.6, ease: EASE }}
      className="card-glow mx-auto max-w-lg rounded-2xl p-8 text-center sm:p-12"
      data-testid="booking-confirmed-panel"
    >
      <motion.span
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.15, type: "spring", stiffness: 200, damping: 14 }}
        className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-[#e6b800] text-[#070709]"
        data-testid="booking-confirmed-icon"
      >
        <Check size={30} strokeWidth={3} />
      </motion.span>
      <h2 className="mt-6 font-display text-2xl font-extrabold uppercase text-white sm:text-3xl">
        You&apos;re in the queue
      </h2>
      {booking.founding_number ? (
        <div
          className="mx-auto mt-5 inline-flex items-center gap-2 rounded-full border border-[#e6b800]/50 bg-[#e6b800]/10 px-5 py-2"
          data-testid="founding-owner-badge"
        >
          <BadgeCheck size={16} className="text-[#e6b800]" />
          <span className="font-mono2 text-[11px] font-bold uppercase tracking-[0.18em] text-[#e6b800]">
            Founding Owner #{booking.founding_number}
          </span>
        </div>
      ) : null}
      <p className="mt-3 text-sm leading-relaxed text-neutral-400 sm:text-base">
        {booking.name}, your {booking.model} {booking.variant} in {booking.color} is confirmed. A confirmation has been
        sent to <span className="text-[#e6b800]">{booking.email}</span>.
      </p>

      <div className="mt-8 rounded-xl border border-white/10 bg-[#0a0a0e] p-5">
        <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-neutral-500">Booking reference</p>
        <div className="mt-2 flex items-center justify-center gap-3">
          <p className="font-mono2 text-xl font-semibold tracking-[0.1em] text-white" data-testid="booking-confirmed-ref">
            {booking.reference}
          </p>
          <button
            onClick={copy}
            data-testid="booking-copy-ref-button"
            className="flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 text-neutral-400 transition-colors hover:border-[#e6b800]/60 hover:text-[#e6b800]"
            aria-label="Copy booking reference"
          >
            {copied ? <Check size={13} /> : <Copy size={13} />}
          </button>
        </div>
        <p className="mt-3 font-mono2 text-[10px] uppercase tracking-[0.2em] text-neutral-600">
          {formatINR(booking.token_amount)} token paid · Refundable anytime
        </p>
      </div>

      <Link
        to="/"
        data-testid="booking-back-home-button"
        className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/15 px-7 py-3.5 font-semibold text-white transition-all duration-200 hover:border-[#e6b800]/60 hover:text-[#e6b800]"
      >
        Back to home <ArrowRight size={16} />
      </Link>
    </motion.div>
  );
}

function BookingFlow({ modelConfig }) {
  const [step, setStep] = useState("Details");
  const [booking, setBooking] = useState(null);
  const [user, setUser] = useState(undefined);

  const loadUser = () => {
    api
      .get("/auth/me", { withCredentials: true })
      .then(({ data }) => setUser(data))
      .catch(() => setUser(null));
  };

  useEffect(() => {
    loadUser();
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  return (
    <>
      <div className="mt-10 flex justify-center">
        <Steps step={step} />
      </div>

      <div className="mx-auto mt-12 grid max-w-5xl gap-10 lg:grid-cols-5">
        <div className="lg:col-span-3">
          <AnimatePresence mode="wait">
            {step === "Details" && (
              <motion.div
                key="details"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.4, ease: EASE }}
                data-testid="booking-step-details"
              >
                {user === undefined ? (
                  <div className="flex justify-center py-16" data-testid="booking-auth-loading">
                    <div className="h-10 w-10 animate-spin rounded-full border-2 border-[#e6b800]/30 border-t-[#e6b800]" />
                  </div>
                ) : user === null ? (
                  <div data-testid="booking-auth-gate">
                    <div className="card-glow rounded-2xl p-6 text-center sm:p-8">
                      <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl border border-[#e6b800]/30 bg-[#e6b800]/10 text-[#e6b800]">
                        <Lock size={20} />
                      </span>
                      <h2 className="mt-4 font-display text-xl font-bold uppercase text-white">Booking से पहले login</h2>
                      <p className="mx-auto mt-2 max-w-sm text-xs leading-relaxed text-neutral-400">
                        Login करें या नया account बनाएँ — आपकी booking account से जुड़ेगी और /account पर दिखेगी।
                      </p>
                    </div>
                    <div className="mt-6">
                      <AuthForms
                        onAuthed={loadUser}
                        showOwnerLink={false}
                        note="Booking इसी email से जुड़ेगी — confirmation और आपकी bookings इसी पर दिखेंगी।"
                      />
                    </div>
                  </div>
                ) : (
                  <DetailsForm modelConfig={modelConfig} user={user} onDone={(b) => { setBooking(b); setStep("Payment"); }} />
                )}
              </motion.div>
            )}
            {step === "Payment" && booking && (
              <motion.div
                key="payment"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.4, ease: EASE }}
                data-testid="booking-step-payment"
              >
                <RazorpayCheckout
                  booking={booking}
                  onPaid={(b) => { setBooking(b); setStep("Confirmed"); }}
                  onBack={() => setStep("Details")}
                />
              </motion.div>
            )}
            {step === "Confirmed" && booking && (
              <motion.div
                key="confirmed"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: EASE }}
                data-testid="booking-step-confirmed"
              >
                <Confirmed booking={booking} />
                <TestDriveScheduler booking={booking} onScheduled={(b) => setBooking(b)} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <aside className="lg:col-span-2">
          <div className="sticky top-24 space-y-4" data-testid="booking-perks">
            {PERKS.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.08}>
                <div className="flex gap-4 rounded-2xl border border-white/10 bg-[#111116] p-5 transition-colors duration-300 hover:border-[#e6b800]/40">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[#e6b800]/30 bg-[#e6b800]/10 text-[#e6b800]">
                    <p.icon size={18} />
                  </span>
                  <div>
                    <p className="font-display text-sm font-bold uppercase tracking-wide text-white">{p.title}</p>
                    <p className="mt-1 text-sm leading-relaxed text-neutral-400">{p.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.24}>
              <div className="rounded-2xl border border-[#e6b800]/25 bg-[#e6b800]/5 p-5">
                <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-[#e6b800]">Order summary</p>
                <div className="mt-3 flex items-center justify-between text-sm">
                  <span className="text-neutral-400">{modelConfig.name} · Token deposit</span>
                  <span className="font-semibold text-white">{formatINR(modelConfig.token)}</span>
                </div>
                {modelConfig.discount !== false && (
                  <div className="mt-2 flex items-center justify-between text-sm" data-testid="discount-row">
                    <span className="text-neutral-400">Launch offer · 15% OFF</span>
                    <span className="font-semibold text-[#e6b800]">Applied</span>
                  </div>
                )}
                <div className="mt-2 flex items-center justify-between text-sm">
                  <span className="text-neutral-400">Refundable</span>
                  <span className="text-[#e6b800]">100%</span>
                </div>
                <div className="mt-2 flex items-center justify-between text-sm" data-testid="delivery-row">
                  <span className="text-neutral-400">Expected delivery</span>
                  <span className="font-semibold text-[#e6b800]">January 2028</span>
                </div>
              </div>
            </Reveal>
          </div>
        </aside>
      </div>
    </>
  );
}

function ModelHub() {
  return (
    <div className="mx-auto mt-12 grid max-w-5xl gap-6 md:grid-cols-3" data-testid="model-hub">
      {HUB_MODELS.map((m, i) => (
        <Reveal key={m.slug} delay={i * 0.08} className="h-full">
          <Link
            to={`/booking/${m.slug}`}
            data-testid={`model-hub-card-${m.slug}`}
            className="group flex h-full flex-col overflow-hidden rounded-2xl border border-white/10 bg-[#111116] transition-all duration-300 hover:-translate-y-1.5 hover:border-[#e6b800]/60"
          >
            <div className="relative overflow-hidden">
              <img src={m.hero} alt={m.name} className="h-44 w-full object-cover transition-transform duration-700 group-hover:scale-105" />
              {m.ev && (
                <span className="absolute left-3 top-3 rounded-full bg-[#e6b800] px-2.5 py-1 font-mono2 text-[9px] font-bold uppercase tracking-[0.15em] text-black">
                  Electric
                </span>
              )}
            </div>
            <div className="flex flex-1 flex-col p-5">
              <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-[#e6b800]">{m.tag}</p>
              <h3 className="mt-1 font-display text-lg font-bold text-white">{m.name}</h3>
              <p className="mt-2 text-xs text-neutral-400">
                {m.noVariants
                  ? `Single variant · ${formatINR(m.price)}* · Limited availability`
                  : `${m.variants.map((v) => v.id).join(" · ")} — from ${m.variants[0].ex}${m.discount !== false ? " · 15% OFF" : ""}`}
              </p>
              <p className="mt-3 font-mono2 text-[10px] uppercase tracking-[0.18em] text-[#e6b800]">
                Reserve for {formatINR(m.token)}
                <ArrowRight size={12} className="ml-1 inline transition-transform duration-200 group-hover:translate-x-0.5" />
              </p>
            </div>
          </Link>
        </Reveal>
      ))}
    </div>
  );
}

export default function Booking() {
  const { slug } = useParams();
  const modelConfig = MODEL_CONFIG[slug];
  const [count, setCount] = useState(null);

  useEffect(() => {
    api
      .get("/bookings/count")
      .then((res) => setCount(res.data.confirmed))
      .catch(() => setCount(null));
  }, []);

  return (
    <section className="mx-auto max-w-7xl px-5 pb-28 pt-32 sm:px-8" data-testid="booking-page">
      <Reveal className="text-center" testId="booking-header">
        <p className="eyebrow">Pre-Booking</p>
        <h1 className="mt-4 font-display text-3xl font-extrabold uppercase tracking-tight text-white sm:text-5xl">
          Reserve your <span className="text-gold-gradient">IshuCar</span>
        </h1>
        <p className="mx-auto mt-4 max-w-xl text-base text-neutral-400 sm:text-lg">
          {count > 0
            ? `Join ${count} ${count === 1 ? "owner" : "owners"} already in the queue.`
            : "Be among the first owners."}{" "}
          Refundable token {modelConfig ? `of ${formatINR(modelConfig.token)}` : "from ₹10,000"}.
        </p>
      </Reveal>

      {modelConfig ? <BookingFlow modelConfig={modelConfig} /> : <ModelHub />}

      <BookingLookup />
    </section>
  );
}